import { useCallback, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPlatformQueryClient } from "@/lib/providers/query-provider";

export const INTAKE_QUERY_DEFAULTS = {
  staleTime: 0,
  gcTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: false as const,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000),
};

/** Platform dashboard/monitoring reads — do not override intake staleTime: 0. */
export const PLATFORM_QUERY_DEFAULTS = {
  staleTime: 30_000,
  gcTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: false as const,
};

/** Single monitoring metrics poll interval (Monitoring page + useSystemMetrics). */
export const MONITORING_METRICS_POLL_MS = 30_000;

/** Command home dashboard overview poll (ModernDashboard). */
export const HOME_DASHBOARD_POLL_MS = 60_000;

/** Analytics AI tab auto-refresh interval. */
export const ANALYTICS_SNAPSHOT_POLL_MS = 30_000;

/** Intake dashboard silent refresh interval. */
export const INTAKE_DASHBOARD_POLL_MS = 60_000;

/** Service status card poll interval. */
export const SERVICE_STATUS_POLL_MS = 30_000;

/** Frontend navigation/config cache — Sidebar + ModuleRails. */
export const FRONTEND_CONFIG_QUERY_KEY = ["frontend-config"] as const;
export const FRONTEND_CONFIG_STALE_MS = 15 * 60_000;

/** Optional list-read stale window for platform list hooks (not global intake default). */
export const INTAKE_LIST_STALE_MS = 30_000;

let activeTenantQueryScope = "";
const activeTenantQueryScopeListeners = new Set<() => void>();

/** Organization id stamped on React Query keys. TenantProvider.applyPayload is the only writer. */
export function publishActiveTenantQueryScope(tenantId: string): void {
  if (activeTenantQueryScope === tenantId) {
    return;
  }
  activeTenantQueryScope = tenantId;
  for (const listener of activeTenantQueryScopeListeners) {
    listener();
  }
}

export function readActiveTenantQueryScope(): string {
  return activeTenantQueryScope;
}

export function intakeListReturnQueryKey(baseKey: string): string {
  const tenant = readActiveTenantQueryScope().trim() || "unset";
  return `${baseKey}:${tenant}`;
}

export function subscribeActiveTenantQueryScope(listener: () => void): () => void {
  activeTenantQueryScopeListeners.add(listener);
  return () => {
    activeTenantQueryScopeListeners.delete(listener);
  };
}

export function useActiveTenantQueryScope(): string {
  return useSyncExternalStore(
    subscribeActiveTenantQueryScope,
    readActiveTenantQueryScope,
    readActiveTenantQueryScope,
  );
}

export function tenantScopedQueryKey(tenantId: string, queryKey: readonly unknown[]): readonly unknown[] {
  return [tenantId, ...queryKey];
}

export function sameTenantPlaceholder<T>(
  tenantId: string,
  previous: T | undefined,
  previousQuery: { queryKey: readonly unknown[] } | undefined,
): T | undefined {
  if (previousQuery === undefined) {
    return undefined;
  }
  if (previousQuery.queryKey[0] !== tenantId) {
    return undefined;
  }
  return previous;
}

export function tenantQueryEnabled(callerEnabled: boolean | undefined, tenantId: string): boolean {
  if (tenantId.length === 0) {
    return false;
  }
  if (callerEnabled === undefined) {
    return true;
  }
  return callerEnabled;
}

export interface PlatformApiQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function ensurePlatformApiData<T>(response: {
  success: boolean;
  data?: T | null;
  error?: unknown;
  message?: string;
}): T {
  if (!response.success) {
    const failure =
      typeof response.error === "string"
        ? response.error
        : typeof response.message === "string"
          ? response.message
          : "Request failed";
    throw new Error(failure);
  }
  if (response.data === undefined || response.data === null) {
    throw new Error("Response did not include data");
  }
  return response.data;
}

export function usePlatformApiQuery<T>(
  queryKey: readonly unknown[],
  fetcher: () => Promise<{ success: boolean; data?: T | null; error?: unknown; message?: string }>,
): PlatformApiQueryResult<T> {
  const tenantId = useActiveTenantQueryScope();
  const query = useQuery({
    queryKey: tenantScopedQueryKey(tenantId, queryKey),
    enabled: tenantQueryEnabled(undefined, tenantId),
    queryFn: async () => ensurePlatformApiData(await fetcher()),
    ...PLATFORM_QUERY_DEFAULTS,
  });
  const refetch = useCallback(async () => {
    await query.refetch();
  }, [query.refetch]);
  return {
    data: query.data ?? null,
    loading: query.isPending && query.data === undefined,
    error:
      query.error instanceof Error
        ? query.error.message
        : query.error
          ? String(query.error)
          : null,
    refetch,
  };
}

export async function fetchIntakeListQuery<T>(options: {
  queryKey: readonly unknown[];
  queryFn: () => Promise<T>;
  staleTime?: number;
}): Promise<T> {
  const tenantId = readActiveTenantQueryScope();
  const staleTime = options.staleTime !== undefined ? options.staleTime : INTAKE_LIST_STALE_MS;
  return getPlatformQueryClient().fetchQuery({
    queryKey: tenantScopedQueryKey(tenantId, options.queryKey),
    queryFn: options.queryFn,
    staleTime,
  });
}
